import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Drawer,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Fab,
  Zoom,
  Paper,
  Button,
  Alert,
  Snackbar,
  Backdrop,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import { LoaderWrapper } from "../../components/index";

// Import API services
import {
  getApplicationForm,
  saveApplicationForm,
  submitApplication,
  getLabById,
  getApplicationId,
} from "../../services/api";

// Import all DO sections
import EmailNotificationSection from "./application/EmailNotificationTemplate";
import InstructionMasterSection from "./application/InstructionMaster";
import AdminDashboard from "./AdminDashboard";
import CategoryManagement from "./CategoryManagement";
import SubCategoryManagement from "./SubCategoryManagement";
import QuestionsManagement from "./QuestionsManagement";
import ComplianceManagement from "./ComplianceManagement";
import ListFormManagement from "./ListFormManagement";
import NCStatementsManagement from "./NCStatementsManagement";
import ScrutinyRemarksManagement from "./ScrutinyRemarksManagement";
import AssessorEditManagement from "./AssessorEditManagement";
import AssessorEditById from "./AssessorEditById";
import AssessorProfile from "./AssessorProfile";
import ScopeMaster from "./application/Adminmasters/ScopeMaster";
import EquipmentMaster from "./application/Adminmasters/EquipmentMaster";
import PTLaboratory from "./application/Adminmasters/PTLaboratoryMaster";
import PTOther from "./application/Adminmasters/PTOtherMaster";
import AbreviationMaster from "./application/Adminmasters/AbreviationMaster";
import ACDates from "./application/Adminmasters/ACDates";
import CreateUsers from "./Users/CreateUsers";
import AdminPasswordReset from "./AdminPasswordReset";
import StageUpdate from "./StageUpdate";
import JDDOMapping from "./JDDOMapping";
import DisciplineMaster from "./application/Adminmasters/DisciplineMaster";
import GroupMaster from "./application/Adminmasters/GroupMaster";
import SubGroupMaster from "./application/Adminmasters/SubGroupMaster";
import MarkLabStatus from "./application/Adminmasters/MarkLabStatus";

const DRAWER_WIDTH = 320;
const COLLAPSED_DRAWER_WIDTH = 70;
const APP_HEADER_HEIGHT = 64; // Height of the existing ApplicationHeader

const DoLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [currentSection, setCurrentSection] = useState(
    "email-notification-template",
  );
  const [formData, setFormData] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState(null);
  const [labData, setLabData] = useState(null);
  const [appId, setAppId] = useState(null);
  const [showDeclaration, setShowDeclaration] = useState(false);

  // Section configuration
  const sections = useMemo(
    () => [
      {
        id: "dashboard",
        title: "Dashboard",
        component: AdminDashboard,
        path: "/main/superAdmin/dashboard",
      },
      {
        id: "create-users",
        title: "Create Users",
        component: CreateUsers,
        path: "/main/superAdmin/create-users",
      },
      {
        id: "admin-password-reset",
        title: "Admin Password Reset",
        component: AdminPasswordReset,
        path: "/main/superAdmin/admin-password-reset",
      },
      {
        id: "stage-update",
        title: "Stage Update",
        component: StageUpdate,
        path: "/main/superAdmin/stage-update",
      },
      {
        id: "category-management",
        title: "Category Management",
        component: CategoryManagement,
        path: "/main/superAdmin/category-management",
      },
      {
        id: "sub-category-management",
        title: "Sub-Category Management",
        component: SubCategoryManagement,
        path: "/main/superAdmin/sub-category-management",
      },
      {
        id: "questions-management",
        title: "Questions Management",
        component: QuestionsManagement,
        path: "/main/superAdmin/questions-management",
      },
      {
        id: "compliance-management",
        title: "Compliance Management",
        component: ComplianceManagement,
        path: "/main/superAdmin/compliance-management",
      },
      {
        id: "listform-management",
        title: "List/Form Management",
        component: ListFormManagement,
        path: "/main/superAdmin/listform-management",
      },
      {
        id: "listform-management",
        title: "List/Form Management",
        component: ListFormManagement,
        path: "/main/superAdmin/listform-management",
      },
      {
        id: "ncstatements-management",
        title: "NC Statements Management",
        component: NCStatementsManagement,
        path: "/main/superAdmin/ncstatements-management",
      },
      {
        id: "scrutiny-remarks-management",
        title: "Scrutiny Remarks Management",
        component: ScrutinyRemarksManagement,
        path: "/main/superAdmin/scrutiny-remarks-management",
      },
      {
        id: "assessor-management",
        title: "Assessor Management",
        component: AssessorEditManagement,
        path: "/main/superAdmin/assessor-management",
      },
      {
        id: "email-notification-template",
        title: "Email Notification Template",
        component: EmailNotificationSection,
        path: "/main/superAdmin/email-notification-template",
      },
      {
        id: "instruction-master",
        title: "Instruction Master",
        component: InstructionMasterSection,
        path: "/main/superAdmin/instruction-master",
      },
      {
        id: "assessor-edit",
        title: "Assessor Edit",
        component: AssessorEditById,
        path: "/main/superAdmin/assessor-edit",
      },
      {
        id: "assessor-profile",
        title: "Assessor Profile",
        component: AssessorProfile,
        path: "/main/superAdmin/assessor-profile",
      },
      {
        id: "scope-master",
        title: "Scope Master",
        component: ScopeMaster,
        path: "/main/superAdmin/scope-master",
      },
      {
        id: "equipment-master",
        title: "Equipment Master",
        component: EquipmentMaster,
        path: "/main/superAdmin/equipment-master",
      },
      {
        id: "pt-laboratory",
        title: "PT Laboratory",
        component: PTLaboratory,
        path: "/main/superAdmin/pt-laboratory-master",
      },
      {
        id: "pt-other",
        title: "PT Other",
        component: PTOther,
        path: "/main/superAdmin/pt-other-master",
      },
      {
        id: "abreviation-master",
        title: "Abbreviation Master",
        component: AbreviationMaster,
        path: "/main/superAdmin/abreviation-master",
      },
      {
        id: "ac-dates",
        title: "AC Dates",
        component: ACDates,
        path: "/main/superAdmin/ac-dates",
      },
      {
        id: "jddo-mapping",
        title: "JD-DO Mapping",
        component: JDDOMapping,
        path: "/main/superAdmin/jddo-mapping",
      },
      {
        id: "discipline-master",
        title: "Discipline Master",
        component: DisciplineMaster,
        path: "/main/superAdmin/discipline-master",
      },
      {
        id: "group-master",
        title: "Group Master",
        component: GroupMaster,
        path: "/main/superAdmin/group-master",
      },
      {
        id: "sub-group-master",
        title: "Sub Group Master",
        component: SubGroupMaster,
        path: "/main/superAdmin/sub-group-master",
      },
      {
        id: "mark-lab-status",
        title: "Mark Lab Status",
        component: MarkLabStatus,
        path: "/main/superAdmin/mark-lab-status",
      },
    ],
    [],
  );

  // Determine current section based on route
  useEffect(() => {
    const currentPath = location.pathname;
    const section = sections.find((s) => s.path === currentPath);
    if (section) {
      setCurrentSection(section.id);
    }
  }, [location.pathname, sections]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFormDataUpdate = async (sectionId, data) => {
    const updatedFormData = {
      ...formData,
      [sectionId]: {
        ...formData[sectionId],
        ...data,
      },
    };

    setFormData(updatedFormData);
  };

  const navigateToSection = (sectionId) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      navigate(section.path);
    }
  };

  const getCurrentSectionIndex = () => {
    return sections.findIndex((s) => s.id === currentSection);
  };

  const handlePreviousSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex > 0) {
      navigateToSection(sections[currentIndex - 1].id);
    }
  };

  // Submit complete application
  const submitCompleteApplication = async () => {
    try {
      setLoading(true);

      if (!appId) {
        setError("No application ID found. Please refresh the page.");
        return;
      }

      const payload = {
        appId: appId,
        formData: formData,
        labTypeId: 3, // Medical lab type
        completedSections: completedSections,
      };

      await submitApplication(payload);

      // Show success message and redirect
      setSnackbarOpen(true);
      setTimeout(() => {
        navigate("/main/dashboard"); // or wherever you want to redirect after submission
      }, 2000);
    } catch (error) {
      console.error("Error submitting application:", error);
      setError("Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleNextSection = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex < sections.length - 1) {
      navigateToSection(sections[currentIndex + 1].id);
    } else {
      // Last section - submit application
      submitCompleteApplication();
    }
  };

  const renderCurrentSection = () => {
    const section = sections.find((s) => s.id === currentSection);
    if (!section) return null;

    const SectionComponent = section.component;

    return (
      <SectionComponent
        data={formData[currentSection] || {}}
        onUpdate={(data) => handleFormDataUpdate(currentSection, data)}
      />
    );
  };

  const currentSectionIndex = getCurrentSectionIndex();
  const isFirstSection = currentSectionIndex === 0;
  const isLastSection = currentSectionIndex === sections.length - 1;

  const drawerWidth = sidebarCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;

  return (
    <Box
      sx={{ display: "flex", height: "100vh", pt: `${APP_HEADER_HEIGHT}px` }}
    >
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? "temporary" : "persistent"}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRight: "1px solid #e0e0e0",
            height: `calc(100vh - ${APP_HEADER_HEIGHT}px)`,
            top: `${APP_HEADER_HEIGHT}px`,
            display: "flex",
            flexDirection: "column",
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            }),
            overflowX: "hidden",
          },
        }}
      >
        <Sidebar
          isMobile={isMobile}
          onClose={() => setSidebarOpen(false)}
          activeStep={currentSectionIndex}
          onSectionClick={(item) => navigate(item.path)}
          showDeclaration={showDeclaration}
          collapsed={sidebarCollapsed}
          onCollapseToggle={handleSidebarCollapse}
        />
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : "100%" },
          ml: { lg: sidebarOpen ? 0 : 0 },
          transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeInOut,
            duration: theme.transitions.duration.standard,
          }),
          position: "relative",
        }}
      >
        {/* Toggle Sidebar Button for Desktop when Sidebar is Closed */}
        {isMobile && !sidebarOpen && (
          <Box
            sx={{
              position: "fixed",
              top: `${APP_HEADER_HEIGHT + 10}px`,
              left: 10,
              zIndex: theme.zIndex.drawer + 1,
            }}
          >
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                backgroundColor: "white",
                border: "1px solid #e0e0e0",
                boxShadow: 2,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        <Container maxWidth="xl" sx={{ py: 3, height: "100%" }}>
          {/* Loading Indicator */}
          {loading && (
            <LoaderWrapper
              loading={true}
              type="page"
              message="Loading application data..."
            />
          )}

          <Paper sx={{ minHeight: "calc(100vh - 280px)", mb: 3 }}>
            {location.pathname === "/main/superAdmin" ||
            location.pathname === "/main/superAdmin/" ? (
              <AdminDashboard />
            ) : (
              renderCurrentSection()
            )}
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Save Status Indicator */}
          {saveLoading && (
            <LoaderWrapper
              loading={true}
              type="inline"
              message="Saving form data..."
              size="small"
            />
          )}
        </Container>
      </Box>

      {/* Save Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Form data saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DoLayout;
